import { useState } from 'react';
import { Button, Group, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { MealPlan } from '../types/mealPlan';
import { formatDateRange } from './mealPlanUtils';

interface MealPlanDeletedNotificationOptions {
  mealPlan: MealPlan;
  /** Fresh auth token for the undo request. */
  getIdToken: () => Promise<string | undefined>;
  /** Puts the restored plan back in the caller's list. */
  onRestored: () => void | Promise<void>;
}

/**
 * "Meal plan deleted" toast with an Undo button.
 *
 * Deleting a plan is a soft delete the backend can reverse, and the delete
 * itself is not confirmed anywhere in the app — the undo here is what makes
 * a mis-click recoverable, so every delete entry point shows it.
 */
export const showMealPlanDeletedNotification = ({
  mealPlan,
  getIdToken,
  onRestored,
}: MealPlanDeletedNotificationOptions) => {
  const notificationId = `delete-meal-plan-${mealPlan.id}`;
  const dateRange = formatDateRange(mealPlan);

  // A component, not a plain handler, so the button can show its own spinner
  // while the restore request is in flight.
  const UndoButton = () => {
    const [isUndoing, setIsUndoing] = useState(false);

    return (
      <Button
        size="xs"
        variant="subtle"
        color="orange"
        loading={isUndoing}
        onClick={async () => {
          setIsUndoing(true);
          try {
            const response = await fetch(
              `${process.env.REACT_APP_API_URL}/api/meals/plans/${mealPlan.id}/undo`,
              {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${await getIdToken()}`,
                },
              }
            );

            if (!response.ok) {
              throw new Error('Failed to restore meal plan');
            }

            notifications.hide(notificationId);
            await onRestored();
            notifications.show({
              title: 'Meal plan restored',
              message: 'Meal plan has been restored',
              color: 'green',
            });
          } catch (error) {
            console.error('Error restoring meal plan:', error);
            notifications.show({
              title: 'Error',
              message: 'Failed to restore meal plan',
              color: 'red',
            });
          } finally {
            setIsUndoing(false);
          }
        }}
      >
        Undo
      </Button>
    );
  };

  notifications.show({
    id: notificationId,
    title: 'Deleted meal plan',
    message: (
      <Group>
        <Text size="sm">{`Meal plan for ${dateRange} has been deleted`}</Text>
        <UndoButton />
      </Group>
    ),
    color: 'orange',
    autoClose: 5000,
    withCloseButton: true,
  });
};
